import { AfterViewInit, Component, ElementRef, Inject, OnInit, Optional, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'app-capture-image-from-camera',
  templateUrl: './capture-image-from-camera.component.html',
  styleUrls: ['./capture-image-from-camera.component.css'],
  standalone: false
})
export class CaptureImageFromCameraComponent implements AfterViewInit {
  @ViewChild('video', { static: true }) video!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvas', { static: true }) canvas!: ElementRef<HTMLCanvasElement>;

  captures: string[] = [];
  isFrontCamera: boolean = false;
  stream: MediaStream | null = null;
  capturedImage: string;
  pageType: any;
  pageTitle: any;
  isTakePhoto: boolean = false;
  latitude: string;
  longitude: string;
  timestamp: string;

  videoTrack!: MediaStreamTrack;

  zoomLevel = 1;
  minZoom = 1;
  maxZoom = 4;

  private initialPinchDistance = 0;
  private initialZoom = 1;
  isLandscape = false;

  supportsFocus = false;
  focusCapabilities: any = null;

  focusX = 0;
  focusY = 0;
  showFocusRing = false;

  private isPinching = false;
  private focusTimeout: any;

  constructor(
    @Optional() public dialogRef: MatDialogRef<any>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    ) {}

  checkOrientation() {
    this.isLandscape = window.matchMedia('(orientation: landscape)').matches;
  }

  ngOnInit(): void {
    this.checkOrientation();
    window.addEventListener('resize', () => this.checkOrientation());

  }

  ngAfterViewInit() {
    this.startCamera();
  }

  // site visite camera
  startCamera() {
    if (!this.video || !this.video.nativeElement) {
      console.error('Video element is not available.');
      return;
    }

  const facingMode = this.isFrontCamera ? "user" : "environment";
  const constraints = {
    video: { facingMode: facingMode, width: 1280, height: 960 },
    audio: false
  };

  // Stop existing stream if already running
  if (this.stream) {
    this.stopCamera();
  }

  // Access user's camera
  navigator.mediaDevices.getUserMedia(constraints)
    .then((stream) => {
      this.stream = stream;
      this.video.nativeElement.srcObject = stream;
      this.videoTrack = stream.getVideoTracks()[0];

      // ✅ Check hardware zoom support
      const capabilities: any = this.videoTrack.getCapabilities();
      this.focusCapabilities = capabilities;

      this.supportsFocus =
        !!capabilities.pointsOfInterest ||
        !!capabilities.focusMode ||
        !!capabilities.focusDistance;

      if (capabilities.zoom) {
        this.minZoom = capabilities.zoom.min;
        this.maxZoom = capabilities.zoom.max;
        this.zoomLevel = capabilities.zoom.min;
      }
      this.isTakePhoto = false;
    })
    .catch((error) => {
      console.error('Error accessing media devices.', error);
      alert('Camera access is required for this functionality.');
    });
}

stopCamera() {
  if (this.stream) {
    this.stream.getTracks().forEach(track => track.stop());
    this.stream = null;
    this.isTakePhoto = false;
  }
}

toggleCamera() {
  this.isFrontCamera = !this.isFrontCamera;
  this.startCamera();
}

capture(): void {
  const canvas = this.canvas.nativeElement;
  const video = this.video.nativeElement;

  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;

  const context = canvas.getContext('2d');
  if (context) {
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
  }

  this.capturedImage = canvas.toDataURL('image/png');
  this.captures.push(this.capturedImage);

  this.stopCamera();

  // Replace video feed with the captured image
  video.srcObject = null; // Clear the video stream
  video.poster = this.capturedImage; // Use the captured image as the placeholder
  this.isTakePhoto = true;

  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const geoLocation = {
          latitude: position.coords.latitude.toFixed(6),
          longitude: position.coords.longitude.toFixed(6)
        };
        const timestamp = new Date().toISOString();
        this.latitude = geoLocation.latitude;
        this.longitude = geoLocation.longitude;
        this.timestamp = timestamp;

      },
      (error) => {

      }
    );
  } else {

  }
}

get toggleButtonText(): string {
  return this.isFrontCamera ? 'Switch to Back Camera' : 'Switch to Front Camera';
}

  sendImage() {
    this.stopCamera();
    const result = {
      latitude:this.latitude,
      longitude: this.longitude,
      timestamp:  this.timestamp,
      image: this.capturedImage,
      section_type: this.data['message']
    }
    this.dialogRef.close(result);
  }

  closeModal() {
    this.stopCamera();
    this.dialogRef.close();
  }

  async onZoomSliderChange() {
    if (!this.videoTrack) return;

    const capabilities: any = this.videoTrack.getCapabilities();

    if (capabilities.zoom) {
      await this.videoTrack.applyConstraints({
        advanced: [{ zoom: this.zoomLevel } as any]
      });
    } else {
      // fallback CSS zoom
      this.video.nativeElement.style.transform =
        `scale(${this.zoomLevel})`;
    }
  }

  onTouchStart(event: TouchEvent) {
    if (event.touches.length === 2) {
      this.isPinching = true;
      this.initialPinchDistance = this.getDistance(event.touches);
      this.initialZoom = this.zoomLevel;
    }
  }

  onTouchMove(event: TouchEvent) {
    if (event.touches.length === 2) {
      this.isPinching = true;
      const currentDistance = this.getDistance(event.touches);
      const scale = currentDistance / this.initialPinchDistance;

      this.zoomLevel = Math.min(
        this.maxZoom,
        Math.max(this.minZoom, this.initialZoom * scale)
      );

      this.onZoomSliderChange();
    }
  }

  getDistance(touches: TouchList) {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  async onTapToFocus(event: PointerEvent) {

    if (this.isPinching) {
      this.isPinching = false;
      return;
    }

    // if (!this.supportsFocus) return;
    if (!this.videoTrack) return;
    if (this.videoTrack.readyState !== 'live') return;

    const videoEl = this.video?.nativeElement;
    if (!videoEl) return;

    const rect = videoEl.getBoundingClientRect();

    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Show focus ring
    this.focusX = x;
    this.focusY = y;
    this.showFocusRing = true;

    clearTimeout(this.focusTimeout);
    this.focusTimeout = setTimeout(() => {
      this.showFocusRing = false;
    }, 800);

    try {

      const capabilities = this.focusCapabilities;

      // 1️⃣ BEST: pointsOfInterest (true tap focus)
      if (capabilities?.pointsOfInterest) {

        await this.videoTrack.applyConstraints({
          advanced: [{
            pointsOfInterest: [{
              x: x / rect.width,
              y: y / rect.height
            }]
          }]
        } as any);

        return;
      }

      // 2️⃣ focusMode single-shot
      if (capabilities?.focusMode?.includes('single-shot')) {

        await this.videoTrack.applyConstraints({
          advanced: [{ focusMode: 'single-shot' }]
        } as any);

        return;
      }

      // 3️⃣ Manual focus distance
      if (capabilities?.focusDistance) {

        const min = capabilities.focusDistance.min || 0;
        const max = capabilities.focusDistance.max || 1;

        const normalized = y / rect.height;
        const distance = min + (max - min) * normalized;

        await this.videoTrack.applyConstraints({
          advanced: [{ focusDistance: distance }]
        } as any);

        return;
      }

    } catch (err) {
      console.warn('Focus apply failed:', err);
    }
  }

}
